import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { createTransformedImage } from '../utils';

/**
 * Produces a transformed (rotated/flipped) image data URL from the original image.
 * Uses rAF so the UI stays responsive. Clears output when original is unloaded.
 */
export function useTransformedImage(
  originalLoaded: boolean,
  originalImgRef: RefObject<HTMLImageElement | null>,
  totalRotation: number,
  flipH: boolean,
  flipV: boolean,
): {
  transformedImageUrl: string;
  transformedDimensions: { width: number; height: number };
  isTransforming: boolean;
} {
  const [transformedImageUrl, setTransformedImageUrl] = useState('');
  const [transformedDimensions, setTransformedDimensions] = useState({ width: 0, height: 0 });
  const [isTransforming, setIsTransforming] = useState(false);
  const transformGenerationRef = useRef(0);

  useEffect(() => {
    if (!originalLoaded) {
      setTransformedImageUrl('');
      setTransformedDimensions({ width: 0, height: 0 });
    }
  }, [originalLoaded]);

  useLayoutEffect(() => {
    if (!originalLoaded || !originalImgRef.current) return;

    const generation = ++transformGenerationRef.current;
    setIsTransforming(true);

    const id = requestAnimationFrame(() => {
      const img = originalImgRef.current;
      if (!img || generation !== transformGenerationRef.current) {
        setIsTransforming(false);
        return;
      }
      const result = createTransformedImage(img, totalRotation, flipH, flipV);
      if (generation !== transformGenerationRef.current) {
        setIsTransforming(false);
        return;
      }
      setTransformedImageUrl(result.dataUrl);
      setTransformedDimensions({ width: result.width, height: result.height });
      setIsTransforming(false);
    });
    return () => cancelAnimationFrame(id);
  }, [originalLoaded, totalRotation, flipH, flipV, originalImgRef]);

  return {
    transformedImageUrl,
    transformedDimensions,
    isTransforming,
  };
}
